import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { env } from '@/config/env';

const API_BASE_URL = env.apiUrl;
// Crear instancia de axios
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: env.apiTimeoutMs,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para requests - agregar token de autenticación
apiClient.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('auth_token');
      const authToken = token || localStorage.getItem(env.tokenStorageKey);
      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
);

// Interceptor para responses - manejar errores y refresh token
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as any;

      // Si el error es 401 y no es el endpoint de login
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const refreshToken = localStorage.getItem(env.refreshTokenStorageKey) || localStorage.getItem('refresh_token');
          if (refreshToken) {
            // Intentar refrescar el token
            const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
              refreshToken
            });

            const { accessToken, refreshToken: newRefreshToken } = response.data;

            localStorage.setItem(env.tokenStorageKey, accessToken);
            if (newRefreshToken) {
              localStorage.setItem(env.refreshTokenStorageKey, newRefreshToken);
            }

            // Reintentar la petición original
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          // Si falla el refresh, limpiar tokens y redirigir al login
          localStorage.removeItem(env.tokenStorageKey);
          localStorage.removeItem(env.refreshTokenStorageKey);
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      // Manejar otros errores preservando el objeto original de Axios
      const responseData = error.response?.data as
        | { message?: string; error?: string }
        | undefined;
      const errorMessage =
        responseData?.message ||
        responseData?.error ||
        error.message ||
        "Error desconocido";

      error.message = errorMessage;
      return Promise.reject(error);
    }
);

// Funciones auxiliares para tipos específicos de peticiones
export const apiGet = <T>(url: string, params?: any): Promise<T> => {
  return apiClient.get(url, { params }).then(response => response.data);
};

export const apiPost = <T>(url: string, data?: any): Promise<T> => {
  return apiClient.post(url, data).then(response => response.data);
};

export const apiPut = <T>(url: string, data?: any): Promise<T> => {
  return apiClient.put(url, data).then(response => response.data);
};

export const apiDelete = <T>(url: string): Promise<T> => {
  return apiClient.delete(url).then(response => response.data);
};

export default apiClient;
